import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/rendering/components/leaves/PieChart.tsx
// Pie uses series[0] for the (only) ring in flat mode.
// In GROUPED mode (CR-2026-05-07-001 PR-H), renders an outer ring of accounts
// and an inner ring of holdings — surfaces what's in each account at a glance.
//
// UX revision (CR-2026-05-07-001 follow-up):
//  - Tooltip shows BOTH the slice name and value (was: value only)
//  - Legend lists the OUTER ring (accounts) only — inner ring uses tinted
//    versions of the same color so the legend doesn't double up.
//  - Default height bumped for grouped mode so outer-ring labels don't clip.
//  - Outer ring uses an inline percent label (fits inside) instead of an
//    outside callout that clips against narrow containers.
import { memo } from "react";
import { PieChart as RPieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { ChartFrame } from "./ChartFrame";
import { PALETTE } from "./chartCommon";
import { useLocaleStore } from "../../../stores/localeStore";
import { fmtNumber } from "../../../lib/formatters";
/** Custom legend renderer — recharts' Legend `payload` prop is Omit'd from
 *  its public type in this version, so we render the legend ourselves with
 *  the full account-name + subtotal label per row. */
function CustomLegend({ items }) {
    return (_jsx("ul", { className: "flex flex-wrap gap-x-4 gap-y-1 justify-center pt-2 m-0 p-0 text-[11px]", children: items.map((it, i) => (_jsxs("li", { className: "flex items-center gap-1.5 text-slate-300", children: [_jsx("span", { "aria-hidden": true, className: "inline-block h-2.5 w-2.5 rounded-sm", style: { background: it.color } }), _jsx("span", { children: it.name }), _jsxs("span", { className: "text-slate-500", children: ["\u00B7 ", it.valueLabel] })] }, i))) }));
}
// CR-2026-05-24-001 Batch 8: bucket tiny slices into "Other" so the legend
// stays readable when a pie has a long tail (e.g. 20 expense categories).
// Threshold: anything below MIN_PCT of the total gets collapsed.
const MIN_SLICE_PCT = 2.0;
function bucketTinySlices(items, total) {
    if (total <= 0 || items.length <= 5)
        return items;
    const threshold = (MIN_SLICE_PCT / 100) * total;
    const kept = items.filter((it) => it.value >= threshold);
    const dropped = items.filter((it) => it.value < threshold);
    if (dropped.length < 2)
        return items;
    const otherValue = dropped.reduce((s, d) => s + (d.value ?? 0), 0);
    if (otherValue <= 0)
        return kept;
    return [...kept, { ...items[0], name: `Other (${dropped.length})`, value: otherValue }];
}
function PieChartImpl({ title, subtitle, series = [], groups, height }) {
    const locale = useLocaleStore((s) => s.locale);
    // Grouped mode: outer ring = accounts, inner ring = holdings.
    if (Array.isArray(groups) && groups.length > 0) {
        const resolvedHeight = height ?? 260;
        const outerData = groups.map((g) => ({
            name: g.name,
            value: g.value ?? (g.slices ?? []).reduce((s, x) => s + (x.value ?? 0), 0),
        }));
        const outerTotal = outerData.reduce((s, d) => s + d.value, 0) || 1;
        const innerData = [];
        for (const g of groups) {
            for (const s of g.slices ?? []) {
                innerData.push({
                    // `name` includes both account + holding so the tooltip is
                    // self-explanatory ("401k · SPY") even before formatting.
                    name: `${g.name} · ${s.name}`,
                    value: s.value ?? 0,
                    group: g.name,
                    account: g.name,
                    holding: s.name,
                });
            }
        }
        // Custom legend items: outer ring (accounts) only, with subtotals.
        const legendItems = outerData.map((d, i) => ({
            name: d.name,
            color: PALETTE[i % PALETTE.length],
            valueLabel: fmtNumber(d.value, locale),
        }));
        return (_jsx(ChartFrame, { title: title, subtitle: subtitle, children: _jsx(ResponsiveContainer, { width: "100%", height: resolvedHeight, children: _jsxs(RPieChart, { margin: { top: 8, right: 8, bottom: 8, left: 8 }, children: [_jsx(Pie, { data: innerData, dataKey: "value", nameKey: "name", cx: "50%", cy: "48%", innerRadius: 42, outerRadius: 68, paddingAngle: 1, isAnimationActive: false, children: innerData.map((d, i) => {
                                const groupIdx = groups.findIndex((g) => g.name === d.group);
                                const base = PALETTE[groupIdx >= 0 ? groupIdx % PALETTE.length : i % PALETTE.length];
                                return _jsx(Cell, { fill: base, fillOpacity: 0.55, stroke: "#0f172a", strokeWidth: 1 }, i);
                            }) }), _jsx(Pie, { data: outerData, dataKey: "value", nameKey: "name", cx: "50%", cy: "48%", innerRadius: 78, outerRadius: 108, paddingAngle: 2, isAnimationActive: false, label: ({ value }) => {
                                const pct = (value / outerTotal) * 100;
                                return pct >= 5 ? `${pct.toFixed(0)}%` : "";
                            }, labelLine: false, children: outerData.map((_, i) => (_jsx(Cell, { fill: PALETTE[i % PALETTE.length], stroke: "#0f172a", strokeWidth: 1 }, i))) }), _jsx(Tooltip, { contentStyle: { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#cbd5e1", fontWeight: 500 }, itemStyle: { color: "#e2e8f0" }, formatter: (value, name) => {
                                const num = typeof value === "number" ? fmtNumber(value, locale) : String(value);
                                return [num, String(name ?? "")];
                            } }), _jsx(Legend, { content: _jsx(CustomLegend, { items: legendItems }) })] }) }) }));
    }
    // Flat mode (legacy): single ring from series[0].data
    const flatHeight = height ?? 220;
    const first = series[0];
    const rows = first && Array.isArray(first.data) ? first.data : [];
    const rawData = rows.map((d) => ({ name: String(d.x), value: d.y }));
    const rawTotal = rawData.reduce((s, d) => s + (d.value ?? 0), 0) || 1;
    // Collapse a long tail into "Other (N)" so the legend stays readable.
    const data = bucketTinySlices(rawData, rawTotal);
    const flatTotal = data.reduce((s, d) => s + (d.value ?? 0), 0) || 1;
    const flatLegendItems = data.map((d, i) => ({
        name: d.name,
        color: PALETTE[i % PALETTE.length],
        valueLabel: fmtNumber(d.value ?? 0, locale),
    }));
    return (_jsx(ChartFrame, { title: title, subtitle: subtitle, children: _jsx(ResponsiveContainer, { width: "100%", height: flatHeight, children: _jsxs(RPieChart, { margin: { top: 8, right: 8, bottom: 8, left: 8 }, children: [_jsx(Pie, { data: data, dataKey: "value", nameKey: "name", cx: "50%", cy: "50%", outerRadius: 92, isAnimationActive: false, label: ({ value }) => {
                            const pct = ((value ?? 0) / flatTotal) * 100;
                            return pct >= 5 ? `${pct.toFixed(0)}%` : "";
                        }, labelLine: false, children: data.map((_, i) => (_jsx(Cell, { fill: PALETTE[i % PALETTE.length], stroke: "#0f172a", strokeWidth: 1 }, i))) }), _jsx(Tooltip, { contentStyle: { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#cbd5e1", fontWeight: 500 }, itemStyle: { color: "#e2e8f0" }, formatter: (value, name) => {
                            const num = typeof value === "number" ? fmtNumber(value, locale) : String(value);
                            return [num, String(name ?? "")];
                        } }), _jsx(Legend, { content: _jsx(CustomLegend, { items: flatLegendItems }) })] }) }) }));
}
// Memoised so chat-input keystrokes don't trigger SVG re-renders on every
// chart in the thread. Default shallow comparison is sufficient because
// `props` come from immutable RenderBlocks in the zustand store.
export const PieChart = memo(PieChartImpl);
